import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  Renderer2,
  AfterViewInit,
  HostListener
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { UiAvatarComponent } from '@bling-fe/shared/ui-components/ui-avatar';
import { MentionPayload } from '../../nodes/mention.node';

export interface MentionUser {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  avatar_url?: string | null;
  mention_attachment?: string;
}

@Component({
  selector: 'bl-mention-dropdown',
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, UiAvatarComponent],
  template: `
    <div
      class="mention-dropdown"
      [style.bottom.px]="position.bottom"
      [style.left.px]="position.left"
    >
      <ion-list lines="none">
        <ion-item
          *ngFor="let user of users; let i = index"
          [class.selected]="i === selectedIndex"
          (click)="selectUser(user)"
          button
        >
          <bl-avatar
            slot="start"
            [user]="user"
            [size]="32"
            [clickable]="false"
            [showOnlineIndicator]="false"
          ></bl-avatar>
          <ion-label>{{ user.full_name }}</ion-label>
        </ion-item>
        <ion-item *ngIf="loading">
          <ion-spinner
            name="crescent"
            slot="start"
          ></ion-spinner>
          <ion-label color="medium">
            {{
              'UI_COMPONENTS.RICHTEXT_EDITOR.MENTION_DROPDOWN.LOADING'
                | translate
            }}
          </ion-label>
        </ion-item>
        <ion-item *ngIf="!loading && users.length === 0">
          <ion-label color="medium">
            {{
              'UI_COMPONENTS.RICHTEXT_EDITOR.MENTION_DROPDOWN.NO_RESULTS'
                | translate
            }}
          </ion-label>
        </ion-item>
      </ion-list>
    </div>
  `,
  styles: [
    `
      .mention-dropdown {
        position: fixed;
        z-index: 10000;
        background: var(--ion-background-color, #fff);
        border: 1px solid var(--ion-color-light, #e0e0e0);
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        max-height: 200px;
        overflow-y: auto;
        min-width: 200px;
        max-width: 300px;
      }

      ion-list {
        padding: 4px 0;
      }

      ion-item {
        --padding-start: 12px;
        --padding-end: 12px;
        --min-height: 48px;
        cursor: pointer;
      }

      ion-item.selected {
        --background: var(--ion-color-light, #f4f4f4);
      }

      bl-avatar {
        width: 32px;
        height: 32px;
        margin-right: 12px;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MentionDropdownComponent
  implements OnChanges, OnDestroy, AfterViewInit
{
  /**
   * The bounding rectangle of the cursor/caret position where the mention was triggered.
   * Used to position the dropdown directly above the cursor so users can see both
   * what they're typing and the autocomplete suggestions. The dropdown uses `position: fixed`
   * and calculates its position relative to the viewport using this rect's coordinates.
   */
  @Input() anchorRect: DOMRect | null = null;
  @Input() users: MentionUser[] = [];
  @Input() loading = false;

  @Output() userSelected = new EventEmitter<MentionPayload>();

  selectedIndex = 0;

  position = { bottom: 0, left: 0 };

  /**
   * Reference to the dropdown element when appended to document.body.
   * Used for cleanup in ngOnDestroy.
   *
   * Why append to body?
   * Ionic components (like ion-item, ion-content) use CSS `contain: content` property
   * for performance optimization. This containment creates a new containing block,
   * which breaks `position: fixed` - the element becomes positioned relative to
   * the containing ancestor instead of the viewport. By appending the dropdown
   * directly to document.body, we escape the containment and ensure proper
   * fixed positioning relative to the viewport.
   */
  private dropdownElement: HTMLElement | null = null;
  private isAppendedToBody = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {}

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(
          this.selectedIndex + 1,
          this.users.length - 1
        );
        this.cdr.markForCheck();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.cdr.markForCheck();
        break;

      case 'Enter':
      case 'Tab':
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.users[this.selectedIndex]) {
          this.selectUser(this.users[this.selectedIndex]);
        }
        break;
    }
  }

  ngAfterViewInit(): void {
    this.appendToBody();
  }

  ngOnDestroy(): void {
    this.removeFromBody();
  }

  /**
   * Moves the dropdown element to document.body to escape Ionic's CSS containment.
   * See dropdownElement property for detailed explanation.
   */
  private appendToBody(): void {
    if (this.isAppendedToBody) return;

    const dropdown =
      this.elementRef.nativeElement.querySelector('.mention-dropdown');
    if (dropdown) {
      this.dropdownElement = dropdown;
      this.renderer.appendChild(document.body, dropdown);
      this.isAppendedToBody = true;
    }
  }

  private removeFromBody(): void {
    if (this.dropdownElement && this.isAppendedToBody) {
      try {
        this.renderer.removeChild(document.body, this.dropdownElement);
      } catch {
        // Element might already be removed
      }
      this.isAppendedToBody = false;
      this.dropdownElement = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['anchorRect'] && this.anchorRect) {
      this.updatePosition();
    }

    if (changes['users']) {
      this.selectedIndex = 0;
    }
  }

  private updatePosition(): void {
    if (!this.anchorRect) {
      return;
    }

    const dropdownWidth = 300;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Position above cursor, dropdown expands upward
    const bottom = viewportHeight - this.anchorRect.top + 4;

    // Calculate left position, ensuring dropdown doesn't go off right edge
    let left = this.anchorRect.left;
    if (left + dropdownWidth > viewportWidth) {
      left = Math.max(8, viewportWidth - dropdownWidth - 8);
    }

    this.position = { bottom, left };
  }

  selectUser(user: MentionUser): void {
    if (!user.mention_attachment) {
      console.warn('User does not have mention_attachment:', user.full_name);
      return;
    }

    this.userSelected.emit({
      mentionAttachment: user.mention_attachment
    });
  }
}
